// Screen / tab registry [02 §8.1 "Screen registry", BR-PERM-003; screens of 04–07]. Code-defined,
// synced to `core.screen`/`core.screen_tab` by skm-migrate. Label keys are i18n keys; the role
// editor renders a module's verbs that the screen does not support as disabled (not hidden).
import { PERMISSION_CATALOGUE, isPermissionKey } from '../auth/permissions.ts';
import type { PermissionKey, PermissionModule } from '../auth/permissions.ts';

export const SCREEN_SECTIONS = [
  'dashboard',
  'product',
  'materials',
  'purchasing',
  'orders',
  'production',
  'warehouse',
  'shipping',
  'planning',
  'website',
  'accounting',
  'reports',
  'partners',
  'setup',
  'administration',
] as const;
export type ScreenSection = (typeof SCREEN_SECTIONS)[number];

export type ScreenKind =
  'list' | 'record' | 'wizard' | 'board' | 'dashboard' | 'station' | 'view' | 'setup';

export interface ScreenTab {
  readonly id: string;
  readonly labelKey: string;
  /** Positive sensitive-view grant needed to see the tab (API omits its fields) [02 §8.1]. */
  readonly permission: PermissionKey | null;
}

export interface ScreenDef {
  /** Stable id = help-registry feature id root (e.g. `fabrics.list`). */
  readonly id: string;
  /** Chapter screen id where the spec numbers screens (`ORD-S1`, `D2C-15.2-08`). */
  readonly specId: string | null;
  readonly module: PermissionModule;
  readonly section: ScreenSection;
  readonly kind: ScreenKind;
  readonly route: string;
  readonly labelKey: string;
  /** The module's permissions this screen exercises (rest render disabled). */
  readonly verbs: readonly PermissionKey[];
  readonly tabs: readonly ScreenTab[];
  readonly landingEligible: boolean;
  /**
   * CR-BG2e (W2e.nl-assistant R-NLA-3): feature flag (`core.feature_flag` key) the screen depends on; the shell hides
   * the entry while it is off. Null = always shown (permissions still apply).
   */
  readonly flag: string | null;
}

interface S {
  readonly id: string;
  readonly module: PermissionModule;
  readonly section: ScreenSection;
  readonly kind: ScreenKind;
  readonly route: string;
  readonly specId?: string;
  readonly tabs?: readonly (string | readonly [string, PermissionKey])[];
  readonly landing?: boolean;
  readonly verbs?: readonly PermissionKey[];
  readonly flag?: string;
}

function moduleKeys(module: PermissionModule): PermissionKey[] {
  const d = PERMISSION_CATALOGUE[module];
  return [...d.verbs, ...d.special].map((v: string) => `${module}.${v}`).filter(isPermissionKey);
}

function screen(s: S): ScreenDef {
  return Object.freeze({
    id: s.id,
    specId: s.specId ?? null,
    module: s.module,
    section: s.section,
    kind: s.kind,
    route: s.route,
    labelKey: `screens.${s.id}`,
    verbs: Object.freeze(s.verbs ?? moduleKeys(s.module)),
    tabs: Object.freeze(
      (s.tabs ?? []).map((t) => {
        const [id, permission] = typeof t === 'string' ? [t, null] : t;
        return Object.freeze({ id, labelKey: `screens.${s.id}.tabs.${id}`, permission });
      }),
    ),
    landingEligible: s.landing ?? s.kind === 'list',
    flag: s.flag ?? null,
  });
}

const HIST = ['notes', 'attachments', 'history'] as const;
/** list + record pair: `<module>.list` at /section/slug and `<module>.record` at /section/slug/:id. */
function pair(
  module: PermissionModule,
  section: ScreenSection,
  slug: string,
  tabs: readonly (string | readonly [string, PermissionKey])[] = [],
  ids: { list?: string; record?: string; listSpec?: string; recordSpec?: string } = {},
): ScreenDef[] {
  return [
    screen({
      id: ids.list ?? `${module}.list`,
      module,
      section,
      kind: 'list',
      route: `/${section}/${slug}`,
      ...(ids.listSpec ? { specId: ids.listSpec } : {}),
    }),
    screen({
      id: ids.record ?? `${module}.record`,
      module,
      section,
      kind: 'record',
      route: `/${section}/${slug}/:id`,
      tabs: [...tabs, ...HIST],
      ...(ids.recordSpec ? { specId: ids.recordSpec } : {}),
    }),
  ];
}

const one = (
  id: string,
  module: PermissionModule,
  section: ScreenSection,
  kind: ScreenKind,
  route: string,
  more: Partial<S> = {},
): ScreenDef => screen({ id, module, section, kind, route, ...more });

// ---- 02 platform / 04 masters, partners, PLM, costing ----
const platform: ScreenDef[] = [
  one('dashboard.home', 'dashboard', 'dashboard', 'dashboard', '/', { landing: true }),
  one('masters.reference_lists', 'masters', 'setup', 'setup', '/setup/reference-lists', {
    landing: true,
  }),
  one('masters.size_grids', 'masters', 'setup', 'list', '/setup/size-grids'),
  one('masters.fx_rates', 'fx', 'setup', 'list', '/setup/fx-rates'),
  one('settings.system', 'settings', 'setup', 'setup', '/setup/settings', {
    tabs: ['values', 'history'],
  }),
  one('sequences.list', 'sequences', 'setup', 'list', '/setup/sequences', {
    tabs: ['recent_issues'],
  }),
  ...pair('customers', 'partners', 'customers', [
    'addresses',
    'contacts',
    ['commercial', 'customers.view_commercial'],
    'mappings',
    'documents',
  ]),
  ...pair('suppliers', 'partners', 'suppliers', [
    'addresses',
    'contacts',
    ['banking', 'suppliers.view_banking'],
    'prices',
  ]),
  one('suppliers.quotes', 'suppliers', 'partners', 'list', '/partners/quotes'),
  one('suppliers.price_history', 'suppliers', 'partners', 'view', '/partners/price-history'),
  one('suppliers.development', 'suppliers', 'partners', 'view', '/partners/development-requests'),
  one('suppliers.scorecard', 'suppliers', 'partners', 'view', '/partners/scorecard'),
  ...pair('contractors', 'partners', 'contractors', ['rates', 'capacity']),
  ...pair('carriers', 'partners', 'carriers', ['feed_mapping']),
  ...pair('styles', 'product', 'styles', [
    'colourways',
    'skus',
    'size_grid',
    'bom',
    'specs',
    'costing',
    'development',
    'customer_codes',
    'dpp',
  ]),
  ...pair('specs', 'product', 'pattern-specs', ['measurements', 'gradation', 'sketches']),
  ...pair('development', 'product', 'development', ['requirements', 'approvals']),
  one('development.samples', 'development', 'product', 'board', '/product/samples', {
    landing: true,
  }),
  ...pair('costing', 'product', 'costing', [
    'lines',
    ['totals', 'costing.view_margin'],
    'versions',
    'approvals',
  ]),
  one('costing.actuals', 'costing', 'product', 'list', '/product/actual-costs', {
    tabs: ['weekly_close'],
  }),
  ...pair('programmes', 'planning', 'programmes', ['lineup', 'worksheet', 'milestones', 'orders']),
  one('programmes.board', 'programmes', 'planning', 'board', '/planning/programmes-board'),
  one('dpp.readiness', 'dpp', 'product', 'view', '/product/dpp'),
];

// ---- 05 materials, purchasing, inventory, planning (ids as written in 05) ----
const materials: ScreenDef[] = [
  ...pair('fabrics', 'materials', 'fabrics', [
    'colours',
    'prices',
    'purchase_orders',
    'stock',
    'tests',
  ]),
  one('fabrics.reprice', 'fabrics', 'materials', 'wizard', '/materials/fabrics/reprice'),
  one('fabrics.markup_formulas', 'fabrics', 'setup', 'setup', '/setup/markup-formulas'),
  ...pair('trims', 'materials', 'trims', [
    'variants',
    'prices',
    'purchase_orders',
    'stock',
    'ledger',
  ]),
  ...pair('lots', 'materials', 'lots', [
    'rolls',
    'trace',
    'tests',
    'holds',
    'reservations',
    'movements',
    'affected_styles',
  ]),
  one('lots.roll', 'lots', 'materials', 'record', '/materials/rolls/:id', { tabs: ['movements'] }),
  one('lots.trace', 'lots', 'materials', 'view', '/materials/trace'),
  one('lots.holds', 'lots', 'materials', 'list', '/materials/holds'),
  ...pair('testing', 'materials', 'fabric-tests', ['results', 'reports', 'holds']),
  ...pair('purchasing', 'purchasing', 'orders', [
    'lines',
    'receipts',
    'eta',
    ['sage', 'sage_bridge.view'],
  ]),
  one('purchasing.late', 'purchasing', 'purchasing', 'view', '/purchasing/late'),
  one('purchasing.inbound_list', 'purchasing', 'purchasing', 'list', '/purchasing/inbound'),
  one(
    'purchasing.inbound_record',
    'purchasing',
    'purchasing',
    'record',
    '/purchasing/inbound/:id',
    { tabs: ['lines', 'milestones', 'documents'] },
  ),
  one('purchasing.eta_import', 'purchasing', 'purchasing', 'list', '/purchasing/eta-import'),
  one('receiving.list', 'purchasing', 'warehouse', 'list', '/warehouse/receipts'),
  one('receiving.record', 'purchasing', 'warehouse', 'record', '/warehouse/receipts/:id', {
    tabs: ['rolls', ...HIST],
  }),
  one('receiving.scan', 'purchasing', 'warehouse', 'station', '/warehouse/receipts/scan'),
  one('inventory.stock', 'inventory', 'warehouse', 'list', '/warehouse/stock', { landing: true }),
  one('inventory.ledger', 'inventory', 'warehouse', 'list', '/warehouse/ledger'),
  one('inventory.adjustments', 'inventory', 'warehouse', 'list', '/warehouse/adjustments'),
  one('inventory.reservations', 'inventory', 'warehouse', 'list', '/warehouse/reservations'),
  one('inventory.rollforward', 'inventory', 'warehouse', 'view', '/warehouse/rollforward'),
  one('inventory.valuation', 'inventory', 'warehouse', 'view', '/warehouse/valuation'),
  one('inventory.usage', 'inventory', 'warehouse', 'view', '/warehouse/usage'),
  one('planning.runs', 'planning', 'planning', 'list', '/planning/requirement-runs', {
    landing: true,
  }),
  one('planning.run', 'planning', 'planning', 'record', '/planning/requirement-runs/:id', {
    tabs: ['lines', 'demand', 'coverage', 'suggested_pos', 'inputs'],
  }),
  one('planning.ots_garment', 'planning', 'planning', 'view', '/planning/ots-garment'),
  one('planning.ots_fabric', 'planning', 'planning', 'view', '/planning/ots-fabric'),
  one('planning.aging', 'planning', 'planning', 'view', '/planning/aging'),
  one('control.fabric_position', 'planning', 'planning', 'view', '/planning/fabric-position'),
  one('control.style_materials', 'planning', 'planning', 'view', '/planning/style-materials'),
  one('control.units_vs_fabric', 'planning', 'planning', 'view', '/planning/units-vs-fabric'),
];

// ---- 06 orders, PO import, production, warehouse, shipping, labels, notes (spec ids) ----
const ops: ScreenDef[] = [
  one('orders.list', 'orders', 'orders', 'list', '/orders', { specId: 'ORD-S1' }),
  one('orders.record', 'orders', 'orders', 'record', '/orders/:id', {
    specId: 'ORD-S2',
    tabs: ['lines', 'progress', 'revisions', 'shipments', ...HIST],
  }),
  one('orders.wizard', 'orders', 'orders', 'wizard', '/orders/new', { specId: 'ORD-S3' }),
  one('orders.revise', 'orders', 'orders', 'wizard', '/orders/:id/revise', { specId: 'ORD-S4' }),
  one('po_import.inbox', 'po_import', 'orders', 'list', '/orders/po-import', { specId: 'POI-S1' }),
  one('po_import.document', 'po_import', 'orders', 'record', '/orders/po-import/:id', {
    specId: 'POI-S2',
  }),
  one('po_import.exceptions', 'po_import', 'orders', 'list', '/orders/po-import/exceptions', {
    specId: 'POI-S3',
  }),
  one('po_import.approve', 'po_import', 'orders', 'wizard', '/orders/po-import/:id/approve', {
    specId: 'POI-S4',
  }),
  one('po_import.profiles', 'po_import', 'setup', 'setup', '/setup/po-import-profiles', {
    specId: 'POI-S5',
  }),
  one('cutting.queue', 'cutting', 'production', 'list', '/production/cutting', {
    specId: 'CUT-S1',
  }),
  one('cutting.new', 'cutting', 'production', 'wizard', '/production/cutting/new', {
    specId: 'CUT-S2',
  }),
  one('cutting.record', 'cutting', 'production', 'record', '/production/cutting/:id', {
    specId: 'CUT-S3',
    tabs: ['lines', 'materials', 'confirmations', 'reservations', ...HIST],
  }),
  one(
    'cutting.cut_report',
    'cutting',
    'production',
    'record',
    '/production/cutting/:id/cut-report',
    { specId: 'CUT-S4' },
  ),
  one('cutting.print', 'cutting', 'production', 'view', '/production/cutting/:id/print', {
    specId: 'CUT-S5',
  }),
  one('sewing.list', 'sewing', 'production', 'list', '/production/sewing', { specId: 'SEW-S1' }),
  one('sewing.record', 'sewing', 'production', 'record', '/production/sewing/:id', {
    specId: 'SEW-S2',
  }),
  one('sewing.issue', 'sewing', 'production', 'wizard', '/production/sewing/:id/issue', {
    specId: 'SEW-S3',
  }),
  one('fg_receiving.list', 'fg_receiving', 'production', 'list', '/production/fg-receipts', {
    specId: 'FGR-S1',
  }),
  one('fg_receiving.new', 'fg_receiving', 'production', 'wizard', '/production/fg-receipts/new', {
    specId: 'FGR-S2',
  }),
  one(
    'fg_receiving.mirror',
    'fg_receiving',
    'production',
    'record',
    '/production/fg-receipts/:id',
    { specId: 'FGR-S3' },
  ),
  one('warehouse.documents', 'warehouse', 'warehouse', 'list', '/warehouse/documents', {
    specId: 'WHS-S1',
  }),
  one('warehouse.document', 'warehouse', 'warehouse', 'record', '/warehouse/documents/:id', {
    specId: 'WHS-S2',
  }),
  one('warehouse.scan', 'warehouse', 'warehouse', 'station', '/warehouse/scan', {
    specId: 'WHS-S3',
  }),
  one('shipping.list', 'shipping', 'shipping', 'list', '/shipping', { specId: 'SHP-S1' }),
  one('shipping.items_to_ship', 'shipping', 'shipping', 'view', '/shipping/items-to-ship', {
    specId: 'SHP-S2',
  }),
  one('shipping.record', 'shipping', 'shipping', 'record', '/shipping/:id', {
    specId: 'SHP-S3',
    tabs: ['lines', 'packages', 'containers', 'documents', 'labels', ...HIST],
  }),
  one('shipping.scan_station', 'shipping', 'shipping', 'station', '/shipping/scan', {
    specId: 'SHP-S4',
  }),
  one('shipping.mirror', 'shipping', 'shipping', 'record', '/shipping/mirror/:id', {
    specId: 'SHP-S5',
  }),
  // CR-BG2e (W2e.shipping-docs R-SCREEN-SHD-1): the shipping documents list and the customer document profiles.
  one('shipping_docs.list', 'shipping', 'shipping', 'list', '/shipping/documents', { specId: 'SHD-S1' }),
  one('shipping_docs.profiles', 'shipping', 'setup', 'setup', '/setup/document-profiles', { specId: 'SHD-S2' }),
  one('labels.runs', 'labels', 'shipping', 'list', '/shipping/label-runs', { specId: 'LAB-S1' }),
  one('labels.run', 'labels', 'shipping', 'record', '/shipping/label-runs/:id', {
    specId: 'LAB-S2',
  }),
  one('labels.barcodes', 'labels', 'shipping', 'list', '/shipping/barcodes', { specId: 'LAB-S3' }),
  one('labels.templates', 'labels', 'setup', 'setup', '/setup/label-templates', {
    specId: 'LAB-S4',
  }),
  one('labels.market_rules', 'labels', 'setup', 'setup', '/setup/market-rules', {
    specId: 'LAB-S5',
  }),
  one('notes.list', 'notes', 'shipping', 'list', '/shipping/notes', { specId: 'CDN-S1' }),
  one('notes.record', 'notes', 'shipping', 'record', '/shipping/notes/:id', { specId: 'CDN-S2' }),
];

// ---- 07 D2C: the 24 screens (§15.2) ----
const D2C: readonly [string, string, PermissionModule?][] = [
  ['store_connection', 'setup/store'],
  ['channel_settings', 'setup/channel'],
  ['variant_mapping', 'setup/variants'],
  ['sync_monitor', 'setup/sync'],
  ['web_listings', 'catalogue/listings'],
  ['publish_queue', 'catalogue/publish'],
  ['web_pricing', 'catalogue/pricing'],
  ['release_to_web', 'inventory/release'],
  ['allocation_planning', 'inventory/allocation'],
  ['web_availability', 'inventory/availability'],
  ['oversell_exceptions', 'inventory/oversell'],
  ['order_list', 'orders'],
  ['order_detail', 'orders/:id'],
  ['order_exceptions', 'orders/exceptions'],
  ['returns_refunds', 'orders/returns'],
  ['cancellations', 'orders/cancellations'],
  ['release_to_pick', 'fulfilment/release'],
  ['pick_list', 'fulfilment/pick'],
  ['pack_station', 'fulfilment/pack'],
  ['shipping_label', 'fulfilment/label'],
  ['carrier_manifest', 'fulfilment/manifest'],
  ['tracking_board', 'fulfilment/tracking'],
  ['daily_sales_posting', 'accounting/daily-sales', 'gl'],
  ['payout_reconciliation', 'accounting/payouts', 'bank'],
];
const d2c: ScreenDef[] = D2C.map(([slug, path, module], i) =>
  one(
    `d2c.${slug}`,
    module ?? 'd2c',
    'website',
    slug.endsWith('_station') ? 'station' : 'view',
    `/website/${path}`,
    {
      specId: `D2C-15.2-${String(i + 1).padStart(2, '0')}`,
      landing: slug === 'release_to_web' || slug === 'pick_list',
      // CR-BG2f (R-CORE-D2C-3, W2f.d2c-sync R5; D13): every D2C screen is hidden while `d2c.enabled` is off.
      flag: 'd2c.enabled',
    },
  ),
);

// ---- 07 accounting, reporting; 02 administration ----
const finance: ScreenDef[] = [
  one('gl.dashboard', 'gl', 'accounting', 'dashboard', '/accounting', { landing: true }),
  one('gl.accounts', 'gl', 'accounting', 'list', '/accounting/accounts'),
  ...pair('gl', 'accounting', 'journals', ['lines', 'approvals'], {
    list: 'gl.journals',
    record: 'gl.journal',
  }),
  // CR-BG2d (W2d.posting R-SCREEN-PST-1): posting log and exception queue (BR-PST-002/011).
  one('gl.postings', 'gl', 'accounting', 'list', '/accounting/postings'),
  ...pair('ap', 'accounting', 'ap', ['lines', 'match', 'approvals', 'payments']),
  one('ap.payment_runs', 'ap', 'accounting', 'list', '/accounting/payment-runs'),
  // CR-BG2c (W2c.ap R-SCREEN-AP-1): the run as its own record (was an inline detail of the list).
  one('ap.payment_run', 'ap', 'accounting', 'record', '/accounting/payment-runs/:id', {
    tabs: ['items', 'payments', 'history'],
  }),
  ...pair('ar', 'accounting', 'ar', ['lines', 'receipts', 'deductions']),
  one('ar.deductions', 'ar', 'accounting', 'list', '/accounting/deductions'),
  one('bank.reconciliation', 'bank', 'accounting', 'list', '/accounting/bank'),
  one('tax.codes', 'tax', 'accounting', 'setup', '/accounting/tax'),
  one('fx.revaluation', 'fx', 'accounting', 'view', '/accounting/fx-revaluation'),
  one('periods.close', 'periods', 'accounting', 'view', '/accounting/periods'),
  one('fin_reports.statements', 'fin_reports', 'accounting', 'view', '/accounting/statements'),
  // CR-BG2e (W2e.close R-SCREEN-CLS-1): period close checklist, landed costs, cash forecast, opening balances / cutover.
  one('close.checklist', 'periods', 'accounting', 'view', '/accounting/close'),
  one('close.landed_costs', 'costing', 'accounting', 'list', '/accounting/landed-costs'),
  one('close.cash_forecast', 'fin_reports', 'accounting', 'view', '/accounting/cash-forecast'),
  one('close.opening', 'periods', 'accounting', 'view', '/accounting/opening-balances'),
  one('sage_bridge.batches', 'sage_bridge', 'accounting', 'list', '/accounting/sage-bridge'),
  one('reports.catalogue', 'reports', 'reports', 'list', '/reports', { landing: true }),
  one('reports.builder', 'reports', 'reports', 'wizard', '/reports/builder'),
  // CR-BG2d (W2d.reports-std R4): the standard report set (07 §17.1).
  one('reports.standard', 'reports', 'reports', 'list', '/reports/standard'),
  // CR-BG2e (R-NLA-3): hidden while the flag `ai.nl_reports` is off (AC-NLA-02).
  one('reports.ask', 'reports', 'reports', 'view', '/reports/ask', { flag: 'ai.nl_reports' }),
];

const admin: ScreenDef[] = [
  ...pair('users', 'administration', 'users', ['roles', 'sessions', 'devices']),
  ...pair('roles', 'administration', 'roles', ['permissions', 'screens', 'members', 'sod']),
  one('audit.search', 'audit', 'administration', 'list', '/administration/audit', {
    landing: true,
  }),
  one('devices.list', 'devices', 'administration', 'list', '/administration/devices'),
  one('api_clients.list', 'api_clients', 'administration', 'list', '/administration/api-clients'),
  one('ops.operations', 'ops', 'administration', 'dashboard', '/administration/operations', {
    landing: true,
  }),
  one('help.registry', 'help', 'administration', 'setup', '/administration/help'),
];

export const SCREENS: readonly ScreenDef[] = Object.freeze([
  ...platform,
  ...materials,
  ...ops,
  ...d2c,
  ...finance,
  ...admin,
]);

const BY_ID: ReadonlyMap<string, ScreenDef> = new Map(SCREENS.map((s) => [s.id, s]));

export function screenById(id: string): ScreenDef | undefined {
  return BY_ID.get(id);
}

/** Screens a principal may open: needs `<module>.view` (or the screen's first verb) [BR-PERM-003]. */
export function visibleScreens(permissions: ReadonlySet<PermissionKey>): ScreenDef[] {
  return SCREENS.filter((s) => {
    const view = `${s.module}.view`;
    return isPermissionKey(view) ? permissions.has(view) : s.verbs.some((v) => permissions.has(v));
  });
}

/** Tabs of a screen the principal may see (sensitive tabs need their positive grant). */
export function visibleTabs(
  screenDef: ScreenDef,
  permissions: ReadonlySet<PermissionKey>,
): ScreenTab[] {
  return screenDef.tabs.filter((t) => t.permission === null || permissions.has(t.permission));
}

/** Landing precedence: user pick > primary role > dashboard, among viewable, eligible screens. */
export function resolveLanding(
  permissions: ReadonlySet<PermissionKey>,
  candidates: { readonly user?: string | null; readonly primaryRole?: string | null },
): string {
  const ok = (id: string | null | undefined): id is string => {
    if (!id) return false;
    const s = BY_ID.get(id);
    return !!s && s.landingEligible && visibleScreens(permissions).some((v) => v.id === id);
  };
  if (ok(candidates.user)) return candidates.user;
  if (ok(candidates.primaryRole)) return candidates.primaryRole;
  return 'dashboard.home';
}
